import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, MessageCircle } from "lucide-react";
import { useAllMentorTopics, useCreateMentorTopic, useUpdateMentorTopic, useDeleteMentorTopic, MentorTopic } from "@/hooks/useMentorTopics";
import { format } from "date-fns";

export function MentorTopicsManager() {
  const { data: topics, isLoading } = useAllMentorTopics();
  const createTopic = useCreateMentorTopic();
  const updateTopic = useUpdateMentorTopic();
  const deleteTopic = useDeleteMentorTopic();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingTopic, setEditingTopic] = useState<MentorTopic | null>(null);
  const [deleteConfirmTopic, setDeleteConfirmTopic] = useState<MentorTopic | null>(null);

  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");

  const handleCreate = async () => {
    if (!formName.trim()) return;
    await createTopic.mutateAsync({
      name: formName.trim(),
      description: formDescription.trim() || undefined,
    });
    setFormName("");
    setFormDescription("");
    setShowCreateDialog(false);
  };

  const handleUpdate = async () => {
    if (!editingTopic || !formName.trim()) return;
    await updateTopic.mutateAsync({
      id: editingTopic.id,
      name: formName.trim(),
      description: formDescription.trim() || undefined,
    });
    setEditingTopic(null);
    setFormName("");
    setFormDescription("");
  };

  const handleDelete = async () => {
    if (!deleteConfirmTopic) return;
    await deleteTopic.mutateAsync(deleteConfirmTopic.id);
    setDeleteConfirmTopic(null);
  };

  const handleToggleActive = async (topic: MentorTopic) => {
    await updateTopic.mutateAsync({
      id: topic.id,
      is_active: !topic.is_active,
    });
  };

  const openEditDialog = (topic: MentorTopic) => {
    setEditingTopic(topic);
    setFormName(topic.name);
    setFormDescription(topic.description || "");
  };

  const closeDialog = () => {
    setShowCreateDialog(false);
    setEditingTopic(null);
    setFormName("");
    setFormDescription("");
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Mentor Topics
              </CardTitle>
              <CardDescription>
                Manage the topics that mentors can select for mentorship requests
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Topic
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {topics?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No topics created yet. Click "Add Topic" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Topic</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topics?.map((topic) => (
                  <TableRow key={topic.id}>
                    <TableCell className="font-medium">{topic.name}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {topic.description || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={topic.is_active}
                          onCheckedChange={() => handleToggleActive(topic)}
                          disabled={updateTopic.isPending}
                        />
                        <Badge variant={topic.is_active ? "default" : "secondary"}>
                          {topic.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(topic.updated_at), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(topic)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirmTopic(topic)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || !!editingTopic} onOpenChange={closeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTopic ? "Edit Topic" : "Add New Topic"}
            </DialogTitle>
            <DialogDescription>
              {editingTopic
                ? "Update the topic name and description."
                : "Create a new topic that mentors can select."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Topic Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Business Strategy"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Brief description of what this topic covers..."
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={editingTopic ? handleUpdate : handleCreate}
              disabled={!formName.trim() || createTopic.isPending || updateTopic.isPending}
            >
              {(createTopic.isPending || updateTopic.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingTopic ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmTopic} onOpenChange={() => setDeleteConfirmTopic(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Topic</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirmTopic?.name}"? This action cannot be undone.
              Topics with associated help requests cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
